import { deltaVWordHtml } from '../../engine/delta-v-word';
import { t, tOr } from '../../engine/i18n/i18n';
import { boostLines, dvToEur, formatBoostTime, resourceName } from '../../engine/boosts';
import type { OfferView } from './shop-api';

/**
 * A special-offer card. Two shapes: `tile` (grid cell) and `row` (double-width, used on the Skip-time page).
 *
 * The price block is the point of the card: the OFFER price is large, gold, glowing and sits in front on
 * its own clean plate (never animated apart from the glow); the ORIGINAL price is small, struck through
 * and glitches constantly behind/above it. Once an offer's discounted uses are spent (the title offer
 * keeps selling at full price) the card drops the sale styling entirely.
 */

export interface OfferCardCtx {
  balance: number;
  now: number;
  variant?: 'tile' | 'row';
  escape: (s: string) => string;
  formatAmount: (n: number) => string;
}

export function offerName(o: OfferView): string {
  if (o.kind === 'RESOURCE_PACK' && o.pack) {
    const size = o.id.slice(-1);
    return t('offer.pack.name', { resource: resourceName(o.pack.resource), size: tOr(`offer.pack.size.${size}`, size) });
  }
  return tOr(`offer.${o.id}.name`, o.id);
}

export function offerLines(o: OfferView, formatAmount: (n: number) => string): string[] {
  if (o.boost) return boostLines(o.boost.type, o.boost.params, o.boost.uses);
  if (o.kind === 'TITLE') return [t('offer.random_title.desc', { n: String(o.maxUses) })];
  if (o.pack) return [t('offer.pack.desc', { amount: formatAmount(o.pack.amount), resource: resourceName(o.pack.resource) })];
  return [];
}

export function discountPct(o: OfferView): number {
  return o.originalDv > 0 ? Math.round((1 - o.priceDv / o.originalDv) * 100) : 0;
}

/** Whole-number Delta-V with thousands separators — the same convention as the rest of the shop. */
function dv(n: number, formatAmount: (n: number) => string): string {
  return formatAmount(n);
}

export function offerCardHtml(o: OfferView, ctx: OfferCardCtx): string {
  const { balance, now, escape, formatAmount } = ctx;
  const variant = ctx.variant ?? 'tile';
  const sale = o.onSale;
  const activeMs = o.activeUntil ? new Date(o.activeUntil).getTime() - now : 0;
  const running = activeMs > 0;
  const spent = !sale && o.kind !== 'TITLE'; // a spent once-per-day offer
  const short = balance < o.priceDv;
  const disabled = running || spent || short;
  const art = o.boost?.type === 'MINING' || o.boost?.type === 'ATTACK' || o.boost?.type === 'DEFENCE' ? 'boost' : 'offer';

  let label: string;
  if (running) label = o.boost?.uses != null ? t('offers.activeBattles', { n: String(o.boost.uses) }) : t('offers.active', { time: formatBoostTime(activeMs) });
  else if (spent) label = t('offers.soldOut');
  else if (short) label = t('offers.notEnough');
  else label = t('offers.buy');

  const lines = offerLines(o, formatAmount).map((l) => `<li>${escape(l)}</li>`).join('');
  const usesNote =
    o.kind === 'TITLE'
      ? `<div class="offer-note">${sale ? t('offers.usesLeft', { n: String(o.usesLeft) }) : t('offers.fullPrice')}</div>`
      : '';

  const priceBlock = sale
    ? `
      <div class="offer-prices">
        <div class="offer-was" aria-label="${escape(t('offers.fullPrice'))}"><span class="offer-was-glitch" data-text="${dv(o.originalDv, formatAmount)}">${dv(o.originalDv, formatAmount)}</span></div>
        <div class="offer-now"><span class="offer-now-n" data-offer-price>${dv(o.priceDv, formatAmount)}</span>${deltaVWordHtml()}</div>
        <div class="offer-eur" aria-hidden="true">≈ €${dvToEur(o.priceDv)}</div>
      </div>`
    : `
      <div class="offer-prices offer-prices--plain">
        <div class="offer-now offer-now--plain"><span class="offer-now-n" data-offer-price>${dv(o.priceDv, formatAmount)}</span>${deltaVWordHtml()}</div>
      </div>`;

  return `
    <div class="offer-card offer-card--${variant}${sale ? ' offer-card--sale' : ''}${running ? ' offer-card--active' : ''}" data-card="offer-${o.id}" data-offer-id="${o.id}" data-art="${art}">
      ${sale ? `<div class="offer-ribbon" aria-hidden="true">${t('offers.save', { pct: String(discountPct(o)) })}</div>` : ''}
      <div class="offer-art"><img src="${escape(o.art)}" alt="" draggable="false"></div>
      <div class="offer-body">
        <h3 class="offer-name">${escape(offerName(o))}</h3>
        <ul class="offer-lines">${lines}</ul>
        ${usesNote}
      </div>
      <div class="offer-buy">
        ${priceBlock}
        <button type="button" data-action="buy-offer" data-offer="${o.id}" ${disabled ? 'disabled' : ''}
          class="offer-btn shop-buy bg-gold text-bg font-button min-h-12 px-5 text-sm font-bold rounded disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed touch-manipulation">${escape(label)}</button>
      </div>
    </div>`;
}
